import { AnthropicProvider } from "./anthropic.js";
import { OpenAICompatibleProvider } from "./openai-compatible.js";
import type {
  ModelInfo,
  ModelProvider,
  ProviderCapabilities,
  ProviderConfig,
  ProviderType,
} from "./types.js";
import { WorkersAIProvider } from "./workers-ai.js";

export function createProvider(config: ProviderConfig): ModelProvider {
  switch (config.type) {
    case "anthropic":
      return new AnthropicProvider(config);
    case "openai-compatible":
      return new OpenAICompatibleProvider(config);
    case "cloudflare-workers-ai":
      return new WorkersAIProvider(config);
    default:
      throw new Error(`Unknown provider type: ${(config as ProviderConfig).type}`);
  }
}

/** Effective capabilities of a model = provider defaults overlaid with per-model overrides. */
export function modelCapabilities(cfg: ProviderConfig, modelId: string): ProviderCapabilities {
  const m = cfg.models.find((x) => x.id === modelId);
  return { ...cfg.capabilities, ...m?.capabilities };
}

export interface ProviderPreset {
  key: string;
  label: string;
  type: ProviderType;
  baseUrl: string;
  models: ModelInfo[];
  capabilities: ProviderCapabilities;
  notes?: string;
  /** Cost class applied to every model of the preset that does not set its own. */
  cost: NonNullable<ModelInfo["cost"]>;
  /** Where to get an API key (shown as a link; absent for keyless presets). */
  keyUrl?: string;
}

const base = (o: Partial<ProviderCapabilities>): ProviderCapabilities => ({
  tools: true,
  vision: false,
  thinking: false,
  audio: false,
  promptCaching: false,
  maxContext: 128_000,
  ...o,
});

/** One-click starting points shown in Settings → Models. Prices are editable estimates (USD / 1M tokens). */
const RAW_PRESETS: Array<Omit<ProviderPreset, "cost"> & { cost?: ProviderPreset["cost"] }> = [
  {
    key: "anthropic",
    cost: "paid",
    keyUrl: "https://console.anthropic.com/settings/keys",
    label: "Anthropic (Claude)",
    type: "anthropic",
    baseUrl: "https://api.anthropic.com",
    capabilities: base({ vision: true, thinking: true, promptCaching: true, maxContext: 200_000 }),
    models: [
      { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5", maxOutput: 16000 },
      { id: "claude-opus-5-5", label: "Claude Opus 5.5", maxOutput: 16000 },
      { id: "claude-haiku-5-5", label: "Claude Haiku 5.5", maxOutput: 8192 },
    ],
  },
  {
    key: "openrouter",
    cost: "paid",
    keyUrl: "https://openrouter.ai/keys",
    label: "OpenRouter",
    type: "openai-compatible",
    baseUrl: "https://openrouter.ai/api/v1",
    capabilities: base({ vision: true }),
    models: [{ id: "deepseek/deepseek-chat", label: "DeepSeek Chat" }],
    notes: "Hundreds of models behind one key. Replace the model list with the ones you want.",
  },
  {
    key: "deepseek",
    cost: "paid",
    keyUrl: "https://platform.deepseek.com/api_keys",
    label: "DeepSeek",
    type: "openai-compatible",
    baseUrl: "https://api.deepseek.com/v1",
    capabilities: base({}),
    models: [
      { id: "deepseek-chat" },
      { id: "deepseek-reasoner", capabilities: { thinking: true, tools: false } },
    ],
  },
  {
    key: "openai",
    cost: "paid",
    keyUrl: "https://platform.openai.com/api-keys",
    label: "OpenAI",
    type: "openai-compatible",
    baseUrl: "https://api.openai.com/v1",
    capabilities: base({ vision: true }),
    models: [{ id: "gpt-4.1" }, { id: "gpt-4.1-mini" }],
  },
  {
    key: "qwen-alibaba",
    cost: "paid",
    keyUrl: "https://modelstudio.console.alibabacloud.com/",
    label: "Qwen — Alibaba Cloud Model Studio (DashScope)",
    type: "openai-compatible",
    baseUrl: "https://dashscope-intl.aliyuncs.com/compatible-mode/v1",
    capabilities: base({ maxContext: 128_000 }),
    models: [
      { id: "qwen-plus", label: "Qwen Plus" },
      { id: "qwen-max", label: "Qwen Max" },
      { id: "qwen3-coder-plus", label: "Qwen3 Coder Plus" },
      { id: "qwen-vl-max", label: "Qwen VL Max (vision)", capabilities: { vision: true } },
    ],
    notes:
      "Alibaba's OpenAI-compatible endpoint (international region). Create a key in Model Studio; mainland-China accounts use dashscope.aliyuncs.com instead. Model names change often — check the console.",
  },
  // ---- Run it yourself: no account, no key, no internet needed once the model is downloaded. ----
  {
    key: "ollama-local",
    label: "Ollama (on your own computer)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "http://localhost:11434/v1",
    capabilities: base({ maxContext: 32_000 }),
    models: [
      { id: "qwen3:8b", label: "Qwen3 8B (good Arabic, 8 GB RAM)" },
      { id: "llama3.1:8b", label: "Llama 3.1 8B" },
      { id: "gpt-oss:20b", label: "GPT-OSS 20B (16 GB RAM)" },
      { id: "qwen2.5:3b", label: "Qwen2.5 3B (small, Raspberry Pi)" },
    ],
    notes:
      "Install Ollama (ollama.com), run `ollama pull qwen3:8b`. Runs entirely on your hardware. The app must run on the same network (see docs/HOME-SERVER.md).",
  },
  {
    key: "personal-ai",
    label: "Personal AI (my home server / Raspberry Pi gateway)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "https://ai-home.example.com/v1",
    capabilities: base({ tools: false, maxContext: 8192 }),
    models: [
      { id: "qwen2.5:3b", label: "Qwen2.5 3B (Raspberry Pi 5, 8 GB)" },
      { id: "qwen2.5:7b", label: "Qwen2.5 7B (mini-PC 16 GB)" },
      { id: "qwen2.5:14b", label: "Qwen2.5 14B (32 GB or GPU)" },
      { id: "qwen2.5:32b", label: "Qwen2.5 32B (24 GB GPU)" },
    ],
    notes:
      "Your own AI behind the QN Home Gateway (Home Assistant add-on or docker compose). Base URL = your tunnel address or http://<LAN-IP>:8789/v1, API key = your gateway token. Step by step: docs/PERSONAL-AI.md.",
  },
  {
    key: "lmstudio-local",
    label: "LM Studio (local server)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "http://localhost:1234/v1",
    capabilities: base({ maxContext: 32_000 }),
    models: [{ id: "local-model", label: "Whatever model you loaded" }],
    notes: "LM Studio → Developer → Start server. Easy desktop app for Windows/Mac/Linux.",
  },
  {
    key: "llamacpp-local",
    label: "llama.cpp server (local)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "http://localhost:8080/v1",
    capabilities: base({ maxContext: 8_000 }),
    models: [{ id: "local-model", label: "Model started with llama-server" }],
    notes: "The lightest option for a Raspberry Pi: `llama-server -m model.gguf --port 8080`.",
  },
  {
    key: "vllm-local",
    label: "vLLM (your GPU server)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "http://localhost:8000/v1",
    capabilities: base({ maxContext: 32_000 }),
    models: [{ id: "Qwen/Qwen3-32B", label: "Qwen3 32B (needs a 24 GB+ GPU)" }],
    notes:
      "Fast serving for a machine with a real GPU (see docs/RUNPOD.md for the same engine in the cloud).",
  },
  {
    key: "localai",
    label: "LocalAI (text + images + speech)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "http://localhost:8081/v1",
    capabilities: base({ maxContext: 16_000 }),
    models: [{ id: "gpt-4", label: "Model alias from your LocalAI config" }],
    notes:
      "One self-hosted server that also offers /images/generations, /audio/transcriptions (Whisper) and /audio/speech (Piper): use it for images and voice too.",
  },
  {
    key: "pollinations-free",
    label: "Pollinations (free, no key)",
    cost: "free",
    type: "openai-compatible",
    baseUrl: "https://text.pollinations.ai/openai",
    capabilities: base({ maxContext: 32_000 }),
    models: [{ id: "openai-fast", label: "GPT-OSS 20B (anonymous tier)" }],
    notes:
      "Community service, no account or key. Slower and less reliable than the others; prompts go to a third party.",
  },
  {
    key: "groq-free",
    label: "Groq (free tier)",
    cost: "free",
    keyUrl: "https://console.groq.com/keys",
    type: "openai-compatible",
    baseUrl: "https://api.groq.com/openai/v1",
    capabilities: base({ maxContext: 128_000 }),
    models: [
      { id: "openai/gpt-oss-120b", label: "GPT-OSS 120B" },
      { id: "llama-3.3-70b-versatile", label: "Llama 3.3 70B" },
      { id: "qwen/qwen3-32b", label: "Qwen3 32B" },
      { id: "llama-3.1-8b-instant", label: "Llama 3.1 8B (fast)" },
    ],
    notes:
      "Free tier with rate limits (no card). Very fast. Check the console for current model names.",
  },
  {
    key: "gemini-free",
    label: "Google Gemini (free tier)",
    cost: "free",
    keyUrl: "https://aistudio.google.com/apikey",
    type: "openai-compatible",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
    capabilities: base({ vision: true, maxContext: 1_000_000 }),
    models: [
      { id: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (preview)" },
      { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
      { id: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite" },
    ],
    notes:
      "Free tier from Google AI Studio (rate limited; on the free tier Google may use your prompts to improve its products).",
  },
  {
    key: "openrouter-free",
    label: "OpenRouter (free models)",
    cost: "free",
    keyUrl: "https://openrouter.ai/keys",
    type: "openai-compatible",
    baseUrl: "https://openrouter.ai/api/v1",
    capabilities: base({ maxContext: 64_000 }),
    models: [
      { id: "openrouter/free", label: "Auto: best available free model" },
      { id: "nvidia/nemotron-3-ultra-550b-a55b:free", label: "Nemotron 3 Ultra 550B (free)" },
      { id: "google/gemma-4-31b-it:free", label: "Gemma 4 31B (free)" },
      { id: "nvidia/nemotron-3.5-lightning:free", label: "Nemotron 3.5 Lightning (free)" },
      { id: "thinkingmachines/inkling:free", label: "Inkling (free)" },
    ],
    notes:
      "Only the `:free` models cost nothing; the list changes often. Free models are heavily rate limited.",
  },
  {
    key: "pollinations-key",
    label: "Pollinations (free account key)",
    cost: "quota",
    keyUrl: "https://enter.pollinations.ai",
    type: "openai-compatible",
    baseUrl: "https://gen.pollinations.ai/v1",
    capabilities: base({ maxContext: 128_000 }),
    models: [
      { id: "deepseek/deepseek-v4.1-flash", label: "DeepSeek V4.1 Flash" },
      { id: "qwen/qwen3.8-2.4t-a95b", label: "Qwen3.8 2.4T-A95B" },
      { id: "z-ai/glm-5.3-flash", label: "GLM 5.3 Flash" },
      { id: "nvidia/nemotron-3.5-lightning", label: "Nemotron 3.5 Lightning" },
      { id: "openai/gpt-5.4-nano", label: "GPT-5.4 Nano" },
    ],
    notes:
      "The same catalogue as openwebui.pollinations.ai, through its OpenAI-compatible API. Create a free account key at enter.pollinations.ai; usage is drawn from your free 'pollen' allowance. Use the Update models list button to see everything your key can use.",
  },
  {
    key: "nvidia-nim",
    label: "NVIDIA NIM (free developer credits)",
    cost: "quota",
    keyUrl: "https://build.nvidia.com/settings/api-keys",
    type: "openai-compatible",
    baseUrl: "https://integrate.api.nvidia.com/v1",
    capabilities: base({ maxContext: 128_000 }),
    models: [
      { id: "moonshotai/kimi-k2.6", label: "Kimi K2.6" },
      { id: "deepseek-ai/deepseek-v4.1-flash", label: "DeepSeek V4.1 Flash" },
      { id: "z-ai/glm-5.3-flash", label: "GLM 5.3 Flash" },
      { id: "openai/gpt-oss-20b", label: "GPT-OSS 20B" },
      { id: "nvidia/llama-3.1-nemotron-ultra-253b-v1", label: "Nemotron Ultra 253B" },
    ],
    notes:
      "Free developer credits/rate limit on build.nvidia.com (sign-in with a phone number may be required).",
  },
  {
    key: "sambanova",
    label: "SambaNova Cloud (free tier)",
    cost: "quota",
    keyUrl: "https://cloud.sambanova.ai/apis",
    type: "openai-compatible",
    baseUrl: "https://api.sambanova.ai/v1",
    capabilities: base({ maxContext: 128_000 }),
    models: [
      { id: "Meta-Llama-3.3-70B-Instruct", label: "Llama 3.3 70B" },
      { id: "gpt-oss-120b", label: "GPT-OSS 120B" },
      { id: "DeepSeek-V3.2", label: "DeepSeek V3.2" },
    ],
    notes: "Rate-limited free tier; check the current limits in the SambaNova console.",
  },
  {
    key: "runpod-vllm",
    cost: "paid",
    label: "RunPod / vLLM (self-hosted GPU)",
    type: "openai-compatible",
    baseUrl: "https://api.runpod.ai/v2/<ENDPOINT_ID>/openai/v1",
    capabilities: base({}),
    models: [{ id: "Qwen/Qwen2.5-72B-Instruct" }],
    notes:
      "Serverless vLLM worker. Replace <ENDPOINT_ID> and the model name. See docs/PROVIDERS.md.",
  },
  {
    key: "ollama",
    cost: "free",
    label: "Ollama (local)",
    type: "openai-compatible",
    baseUrl: "http://localhost:11434/v1",
    capabilities: base({ tools: false, maxContext: 8192 }),
    models: [{ id: "qwen2.5:3b" }],
    notes: "Reachable only if the Worker can reach it (e.g. via Cloudflare Tunnel to the Pi).",
  },
  {
    key: "workers-ai-binding",
    cost: "quota",
    label: "Cloudflare Workers AI (built-in, no key)",
    type: "cloudflare-workers-ai",
    baseUrl: "binding://ai",
    capabilities: base({ tools: false, maxContext: 32_000 }),
    models: [
      {
        id: "@cf/meta/llama-3.3-70b-instruct-fp8-fast",
        label: "Llama 3.3 70B",
        capabilities: { tools: true },
      },
      { id: "@cf/meta/llama-3.1-8b-instruct", label: "Llama 3.1 8B" },
      { id: "@cf/qwen/qwen2.5-coder-32b-instruct", label: "Qwen2.5 Coder 32B" },
      { id: "@cf/qwen/qwen3-30b-a3b-fp8", label: "Qwen3 30B-A3B" },
      {
        id: "@cf/qwen/qwen3.8-27b",
        label: "Qwen3.8 27B (262K context)",
        capabilities: { maxContext: 262_144 },
      },
    ],
    notes:
      "Uses the Worker's own AI binding: no API token to manage. Billed to your Cloudflare account.",
  },
  {
    key: "pi-ollama",
    cost: "free",
    label: "Raspberry Pi emergency model (Ollama)",
    type: "openai-compatible",
    baseUrl: "runner://ollama/v1",
    capabilities: base({ tools: false, maxContext: 8192 }),
    models: [{ id: "qwen2.5:3b", label: "Qwen2.5 3B (local)" }],
    notes:
      "Last-resort offline fallback. Reached through your paired Pi runner (nothing is exposed). Start Ollama on the Pi: see docs/PI-LOCAL-MODELS.md.",
  },
  {
    key: "workers-ai",
    cost: "quota",
    keyUrl: "https://dash.cloudflare.com/profile/api-tokens",
    label: "Cloudflare Workers AI",
    type: "cloudflare-workers-ai",
    baseUrl: "https://api.cloudflare.com/client/v4/accounts",
    capabilities: base({ maxContext: 32_000 }),
    models: [{ id: "@cf/meta/llama-3.3-70b-instruct-fp8-fast", label: "Llama 3.3 70B" }],
    notes: "Needs your Cloudflare account id and an API token with Workers AI permission.",
  },
];

/** Hosts that answer without any API key (free anonymous tiers). */
const KEYLESS_HOSTS = new Set(["text.pollinations.ai"]);

/** True when calls to this base URL need an API key (so a provider without one cannot be used yet). */
export function providerNeedsKey(baseUrl: string): boolean {
  if (baseUrl.startsWith("binding://") || baseUrl.startsWith("runner://")) return false;
  try {
    const u = new URL(baseUrl);
    // Plain http is only ever accepted for loopback / LAN services (the API refuses it otherwise), which need no key.
    if (u.protocol === "http:") return false;
    return !KEYLESS_HOSTS.has(u.hostname);
  } catch {
    return true;
  }
}

/** Models inherit the preset's cost class unless they set their own, so a model never looks free by accident. */
export const PROVIDER_PRESETS: ProviderPreset[] = RAW_PRESETS.map((p) => ({
  ...p,
  cost: p.cost ?? "paid",
  models: p.models.map((m) => ({ cost: p.cost ?? "paid", ...m })),
}));

/** Providers that only do speech (Azure Speech): usable for voice, never offered as chat models. */
export function isSpeechOnly(baseUrl: string): boolean {
  try {
    return new URL(baseUrl).hostname.endsWith(".tts.speech.microsoft.com");
  } catch {
    return false;
  }
}
